import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseFormGuardProps {
  isDirty: boolean;
  hasActiveUploads?: boolean;
  onReset?: () => void;
}

interface UseFormGuardReturn {
  // Check if navigation should be blocked and show modal if needed.
  attemptNavigation: (action: () => void) => boolean;
  // Check if reset should be blocked and show modal if needed.
  attemptReset: () => boolean;
  // Modal props for confirmation dialog.
  confirmationModal: {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void;
    onCancel: () => void;
  };
}

export const useFormGuard = ({
  isDirty,
  hasActiveUploads = false,
  onReset,
}: UseFormGuardProps): UseFormGuardReturn => {
  const { t } = useTranslation('common');
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'navigate' | 'reset' | null;
    pendingAction: (() => void) | null;
  }>({
    isOpen: false,
    type: null,
    pendingAction: null,
  });

  const hasUnsavedChanges = isDirty || hasActiveUploads;

  // Handle browser navigation warning only when there are unsaved changes.
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Most browsers will show a generic message
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const attemptNavigation = useCallback(
    (action: () => void): boolean => {
      if (hasUnsavedChanges) {
        setModalState({
          isOpen: true,
          type: 'navigate',
          pendingAction: action,
        });
        return false; // Navigation blocked
      }
      action();
      return true; // Navigation allowed
    },
    [hasUnsavedChanges],
  );

  const attemptReset = useCallback((): boolean => {
    if (hasUnsavedChanges) {
      setModalState({
        isOpen: true,
        type: 'reset',
        pendingAction: onReset || null,
      });
      return false; // Reset blocked
    }
    onReset?.();
    return true; // Reset allowed
  }, [hasUnsavedChanges, onReset]);

  const handleConfirm = useCallback(() => {
    const { pendingAction } = modalState;
    setModalState({ isOpen: false, type: null, pendingAction: null });
    pendingAction?.();
  }, [modalState]);

  const handleCancel = useCallback(() => {
    setModalState({ isOpen: false, type: null, pendingAction: null });
  }, []);

  const isReset = modalState.type === 'reset';

  const confirmationModal = {
    isOpen: modalState.isOpen,
    title: t(
      isReset
        ? 'file_upload.cancel_changes_modal.title'
        : 'file_upload.unsaved_changes_modal.title',
    ),
    message: t(
      isReset ? 'file_upload.cancel_changes_modal.text' : 'file_upload.unsaved_changes_modal.text',
    ),
    confirmText: t(
      isReset
        ? 'file_upload.cancel_changes_modal.confirm'
        : 'file_upload.unsaved_changes_modal.confirm',
    ),
    cancelText: t(
      isReset
        ? 'file_upload.cancel_changes_modal.cancel'
        : 'file_upload.unsaved_changes_modal.cancel',
    ),
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return {
    attemptNavigation,
    attemptReset,
    confirmationModal,
  };
};
